import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../utils/firebase';
import {
  findProjectCardPod,
  parseSequencePods,
  type SequencePodBalance,
} from '../utils/sequenceProjectCard';

export type SequenceProjectCardBalance = {
  loading: boolean;
  refreshing: boolean;
  pod: SequencePodBalance | null;
  dateKey: string | null;
  updatedAt: Date | null;
  error: string | null;
};

const EMPTY: SequenceProjectCardBalance = {
  loading: false,
  refreshing: false,
  pod: null,
  dateKey: null,
  updatedAt: null,
  error: null,
};

/**
 * Latest Sequence Project Card pod from the daily finance snapshot, plus a
 * best-effort live refresh (COO / CFO / admin / EA after functions deploy).
 */
export function useSequenceProjectCardBalance(enabled: boolean): SequenceProjectCardBalance {
  const [state, setState] = useState<SequenceProjectCardBalance>(EMPTY);

  useEffect(() => {
    if (!enabled) {
      setState(EMPTY);
      return;
    }

    setState(current => ({ ...current, loading: !current.pod, error: null }));
    const unsub = onSnapshot(
      doc(db, 'financeSettings', 'dailyFinanceReport'),
      snap => {
        if (!snap.exists()) {
          setState(current => ({
            ...current,
            loading: false,
            pod: null,
            dateKey: null,
            updatedAt: null,
            error: current.error,
          }));
          return;
        }
        const data = snap.data();
        setState(current => ({
          ...current,
          loading: false,
          pod: findProjectCardPod(parseSequencePods(data.sequencePods)),
          dateKey: typeof data.dateKey === 'string' ? data.dateKey : null,
          updatedAt: data.updatedAt?.toDate?.() ?? null,
        }));
      },
      err => {
        setState(current => ({
          ...current,
          loading: false,
          error: err.message || 'Unable to read the Sequence snapshot.',
        }));
      },
    );

    let cancelled = false;
    setState(current => ({ ...current, refreshing: true }));
    void import('../utils/sequenceApi')
      .then(api => api.syncDailyFinanceReport())
      .catch(() => {
        // Snapshot is enough when the caller cannot refresh Sequence (shop, or
        // functions still admin-only). The card still shows the last pull.
      })
      .finally(() => {
        if (!cancelled) setState(current => ({ ...current, refreshing: false, loading: false }));
      });

    return () => {
      cancelled = true;
      unsub();
    };
  }, [enabled]);

  return state;
}
